/* eslint-disable react/prop-types */
// Libraries
import dayjs from 'dayjs'
import { Box, Typography, IconButton } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
// Types and Interfaces
import { FailDetails, FailedFeedback } from '../../../../../../types/massApproval'
// Components
import { LightTooltip } from '../../../../../LighTooltip'

/**
 * Error dictionary mapping fail types to user-friendly messages
 * This is used to display a more understandable error message based on the fail type.
 * Each fail type corresponds to a specific issue that can occur during the mass approval process.
 * - 'TimesheetNotFound': Indicates that the timesheet could not be found.
 * - 'Availability': Indicates an issue with calculating availability.
 * - 'Approval': Indicates a failure during the approval process.
 */
const errorDictionary: Record<FailDetails['failType'], string> = {
	TimesheetNotFound: 'Folha de Ponto Não Encontrada',
	Availability: 'Cálculo da Disponibilidade',
	Approval: 'Aprovação',
}

interface ErrorCellProps {
	item: FailedFeedback
}

/**
 * ErrorCell component
 * This component is responsible for rendering the error information for a failed timesheet approval.
 * It displays a user-friendly error message based on the fail type and provides options to copy the error details or navigate to the timesheet.
 * @param {ErrorCellProps} props - Component props
 * @returns {JSX.Element} The rendered ErrorCell component
 */
const ErrorCell: React.FC<ErrorCellProps> = ({ item }) => {
	return (
		<Box
			sx={{
				height: 'inherit',
				alignSelf: 'stretch',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: '1rem',
				marginBlock: 'auto',
			}}
		>
			<Typography
				sx={{
					fontFamily: 'var(--font-family)',
					fontSize: '12px',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '130%',
					color: 'var(--primary-dark)',
				}}
			>
				{errorDictionary[item.failDetails?.failType]}
			</Typography>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
				<LightTooltip
					describeChild
					title='Copiar Erro para envio ao Suporte'
					placement='top-end'
					slotProps={{
						popper: {
							modifiers: [
								{
									name: 'offset',
									options: {
										offset: [0, 12],
									},
								},
							],
						},
					}}
				>
					<IconButton
						onClick={() => {
							navigator.clipboard.writeText(JSON.stringify(item.failDetails?.err, null, 2) || '')
						}}
						sx={{
							padding: '2px',
							marginLeft: 'auto',
						}}
						aria-label={`Copiar detalhes do erro para a folha de ponto do motorista ${item.driverName} no dia ${dayjs(item.timesheetDate).format('DD/MM/YYYY')}`}
					>
						<ContentCopyIcon
							fontSize='small'
							sx={{ color: 'var(--primary)' }}
						/>
					</IconButton>
				</LightTooltip>
				<LightTooltip
					describeChild
					title='Navegar para a folha de ponto'
					placement='top-end'
					slotProps={{
						popper: {
							modifiers: [
								{
									name: 'offset',
									options: {
										offset: [0, 12],
									},
								},
							],
						},
					}}
				>
					<IconButton
						onClick={() => {
							// navigate to the timesheet in a new tab
							window.open(`/timesheet/${item.timesheetId}`, '_blank')
						}}
						sx={{
							padding: '2px',
							marginLeft: 'auto',
						}}
						aria-label={`Navegar para a folha de ponto do motorista ${item.driverName} no dia ${dayjs(item.timesheetDate).format('DD/MM/YYYY')}`}
					>
						<ChevronRightIcon
							fontSize='small'
							sx={{ color: 'var(--primary)' }}
						/>
					</IconButton>
				</LightTooltip>
			</Box>
		</Box>
	)
}

export default ErrorCell
